import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  BarcodeFormat,
  DecodeHintType,
  NotFoundException,
} from "@zxing/library";

import { sourceCropRect } from "./scanRegion";
import type { DecodedPoint, DecodedResult, Decoder } from "./types";

function makeReader(): BrowserMultiFormatReader {
  const hints = new Map<DecodeHintType, unknown>();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints);
}

class ZxingDecoder implements Decoder {
  private reader: BrowserMultiFormatReader | null = null;
  private controls: { stop: () => void } | null = null;
  private stopRequested = false;

  async start(
    video: HTMLVideoElement,
    onResult: (result: DecodedResult) => void,
  ): Promise<void> {
    this.stopRequested = false;
    this.reader = makeReader();

    // Using decodeFromVideoElement: continuously decodes from the video element
    // until stop() is called. Callback is invoked on each frame.
    const controls = await this.reader.decodeFromVideoElement(
      video,
      (result, err) => {
        if (result) {
          const points: DecodedPoint[] = (result.getResultPoints() ?? []).map(
            (p) => ({ x: p.getX(), y: p.getY() }),
          );
          onResult({ raw: result.getText(), points });
        } else if (err && !(err instanceof NotFoundException)) {
          // NotFoundException = no barcode in this frame, normal — ignore silently.

          console.warn("[scanner] decode error:", err);
        }
      },
    );

    if (this.stopRequested) {
      controls.stop();
      return;
    }
    this.controls = controls;
  }

  stop(): void {
    this.stopRequested = true;
    if (this.controls) {
      this.controls.stop();
      this.controls = null;
    }
    this.reader = null;
  }
}

export const zxingDecoder: Decoder = new ZxingDecoder();

// ── zxing-wasm engine (opt-in) ──────────────────────────────────────────────
// The maintained zxing-cpp engine compiled to WebAssembly. Decodes dense AAMVA
// PDF417 barcodes noticeably better than the pure-JS reader. Loaded lazily via
// dynamic import() inside start() so default (browser-engine) users never pull
// in the wasm, and unit tests that only touch the factory never instantiate it.

// One-time, memoized self-host of the wasm binary from our own origin (not the
// default CDN). A promise (not a boolean) so concurrent first-calls can't race
// past a check-then-act and prepare twice.
let wasmPreparePromise: Promise<void> | null = null;

function ensureWasmPrepared(
  mod: typeof import("zxing-wasm/reader"),
): Promise<void> {
  if (!wasmPreparePromise) {
    wasmPreparePromise = (async () => {
      try {
        const { default: wasmUrl } =
          await import("zxing-wasm/reader/zxing_reader.wasm?url");
        // fireImmediately returns a promise that resolves once the wasm module
        // is actually instantiated — await it so the memoized promise reflects
        // real readiness, not just that prepare was kicked off.
        await mod.prepareZXingModule({
          overrides: { locateFile: () => wasmUrl },
          fireImmediately: true,
        });
      } catch (err) {
        // A rejected promise would stay cached forever, breaking every future
        // scan until a page reload. Clear it so the next start() can retry.
        wasmPreparePromise = null;
        throw err;
      }
    })();
  }
  return wasmPreparePromise;
}

/**
 * Decode raw barcode bytes as ISO-8859-1 (1:1 byte→codepoint for the AAMVA
 * range). AAMVA PDF417 payloads are binary: parsing hinges on the leading "@"
 * compliance byte and the LF/RS/CR control separators. zxing-wasm's
 * `result.text` defaults to "HRI" (human-readable) text mode, which strips
 * those control characters and breaks AAMVA parsing. `result.bytes` is always
 * the raw decoded content regardless of text mode, so decode it directly —
 * matching what the @zxing/browser engine returns from getText().
 */
export function decodeBarcodeBytes(bytes: Uint8Array): string {
  return new TextDecoder("iso-8859-1").decode(bytes);
}

class ZxingWasmDecoder implements Decoder {
  private rafId: number | null = null;
  private stopped = false;
  private decoding = false;
  // Incremented on every start(). Captured per run so a decode/import that
  // resolves after a stop()→start() restart can detect it no longer owns the
  // loop (the shared singleton is reused across modal opens / "try again").
  private runId = 0;

  async start(
    video: HTMLVideoElement,
    onResult: (result: DecodedResult) => void,
  ): Promise<void> {
    const myRun = ++this.runId;
    this.stopped = false;
    this.decoding = false;

    // True only while this specific start() invocation still owns the loop.
    const isCurrent = () => !this.stopped && this.runId === myRun;

    const mod = await import("zxing-wasm/reader");
    if (!isCurrent()) return;

    await ensureWasmPrepared(mod);
    if (!isCurrent()) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("[scanner] could not get 2d canvas context");

    const options: Parameters<typeof mod.readBarcodes>[1] = {
      formats: ["PDF417"],
      tryHarder: true,
      maxNumberOfSymbols: 1,
    };

    // Cap decode attempts so the (smaller, faster) cropped frames don't spin the
    // CPU at full frame rate. ~12.5fps is plenty for a handheld scan.
    const MIN_DECODE_INTERVAL_MS = 80;
    let lastDecodeAt = 0;

    const tick = async () => {
      if (!isCurrent()) return;

      // Map the on-screen viewfinder to source pixels (handles any camera
      // orientation), so we crop exactly what the reticle shows.
      const rect = sourceCropRect(
        video.clientWidth,
        video.clientHeight,
        video.videoWidth,
        video.videoHeight,
      );
      const now = performance.now();
      // One decode in flight at a time; throttle cadence; need a real frame.
      if (
        !this.decoding &&
        rect.width > 0 &&
        now - lastDecodeAt >= MIN_DECODE_INTERVAL_MS
      ) {
        this.decoding = true;
        lastDecodeAt = now;
        // Crop to the viewfinder region (matches ScannerModal's reticle) so we
        // decode only the barcode area, not the whole screen.
        canvas.width = rect.width;
        canvas.height = rect.height;
        ctx.drawImage(
          video,
          rect.x,
          rect.y,
          rect.width,
          rect.height,
          0,
          0,
          rect.width,
          rect.height,
        );
        const imageData = ctx.getImageData(0, 0, rect.width, rect.height);
        try {
          const results = await mod.readBarcodes(imageData, options);
          if (isCurrent() && results.length > 0) {
            const { position: p, bytes } = results[0];
            // Points are crop-local — offset back to full-frame coords so the
            // lock-on overlay (which uses the video's natural size) aligns.
            const points: DecodedPoint[] = [
              p.topLeft,
              p.topRight,
              p.bottomRight,
              p.bottomLeft,
            ].map((pt) => ({ x: pt.x + rect.x, y: pt.y + rect.y }));
            // Decode the raw bytes (not result.text — see decodeBarcodeBytes).
            onResult({ points, raw: decodeBarcodeBytes(bytes) });
          }
        } catch (err) {
          // Per-frame decode failures (no barcode in frame) are normal — log
          // and keep scanning, mirroring the browser decoder's NotFound path.
          console.warn("[scanner] wasm decode error:", err);
        } finally {
          // Clear the in-flight flag only if this run still owns the loop, so a
          // late decode from a superseded run can't clear a newer run's flag.
          if (this.runId === myRun) this.decoding = false;
        }
      }

      if (isCurrent()) {
        this.rafId = requestAnimationFrame(() => void tick());
      }
    };

    this.rafId = requestAnimationFrame(() => void tick());
  }

  stop(): void {
    this.stopped = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.decoding = false;
  }
}

export const zxingWasmDecoder: Decoder = new ZxingWasmDecoder();

// ── automatic engine fallback ───────────────────────────────────────────────
// The wasm engine fetches and instantiates a `.wasm` binary at start(). If that
// load fails — a bad/partial deploy, wrong MIME type, a CDN/network blip — the
// decoder's start() rejects. Without a fallback that surfaces to the user as a
// (misleading) "scanner unavailable" error even though the camera is fine and
// the browser engine would have worked. This wrapper transparently retries the
// scan on a fallback decoder when the primary fails to start, so a runtime
// problem with one engine degrades to the other instead of failing the scan.

/**
 * Compose two decoders into one that starts `primary` and, if `primary.start()`
 * rejects, transparently starts `fallback` with the same video + callback.
 * `stop()` stops whichever decoder is currently active.
 */
export function createFallbackDecoder(
  primary: Decoder,
  fallback: Decoder,
): Decoder {
  let active: Decoder | null = null;
  let stopRequested = false;

  return {
    async start(video, onResult) {
      stopRequested = false;
      try {
        active = primary;
        await primary.start(video, onResult);
      } catch (err) {
        // Tidy up any partial state from the failed primary, then fail over.
        primary.stop();
        // If the caller stopped us while the primary was starting, don't bring
        // the fallback to life behind their back.
        if (stopRequested) {
          active = null;
          return;
        }

        console.warn(
          "[scanner] primary decoder failed to start; falling back to secondary engine:",
          err,
        );
        active = fallback;
        await fallback.start(video, onResult);
      }
    },
    stop() {
      stopRequested = true;
      active?.stop();
      active = null;
    },
  };
}

// The 'wasm' engine, hardened with an automatic fall back to the browser engine
// (which bundles its decoder in JS and so can't fail to load). A stable
// singleton so ScannerModal's decode effect — keyed on the decoder identity —
// doesn't churn across renders.
export const zxingWasmWithFallbackDecoder: Decoder = createFallbackDecoder(
  zxingWasmDecoder,
  zxingDecoder,
);

export type DecoderEngine = "browser" | "wasm";

/** Resolve the decoder for a form's selected engine. Defaults to browser. */
export function getDecoder(engine: DecoderEngine): Decoder {
  return engine === "wasm" ? zxingWasmWithFallbackDecoder : zxingDecoder;
}
